import { encounterCreatureField, inventoryField, posCoord } from '../../shared/array-types.js';
import { readCreatureStatuses, readStatusCasterId } from '../compile/creature-status-reader.js';
import { statusItemIdOf } from './squeeze-at-rest.js';
import { statusPreventsActions } from '../actions/status-action-gate.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
export const LATCHED_STATUS_ID = '72F9CC45-81D0-491B-AC18-32D05F519FA8';
declare global { interface Window { ftLatchChoice?: (actorId: string) => void } }
export const upper = (value: unknown): string => String(Array.isArray(value) ? value[0] ?? '' : value ?? '').trim().toUpperCase();
export const creatureId = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
export const latchObjectId = (row: Row): string => upper(inventoryField(row, 'inventory_id'));
type StatusReader = { statuses: (row: Row) => Row[] };
/** Piloting replaces the displayed speed; a latched operator pulls with its own pre-mount walk. */
export function latchWalkSpeed(row: Row, read: { value: (row: Row, key: string) => unknown }, operator = false): number {
  const value = operator ? record(record(record(record(row.computed)?.creature)?.speeds)?.walking)?.pre_mount : read.value(row, 'speed_walking');
  return Number(Array.isArray(value) ? value[0] : value) || 0;
}
/** A voluntary pull needs a living, action-capable walker; forced object moves carry everyone. */
export function latchCanWalk(row: Row, read: StatusReader & {
  value: (row: Row, key: string) => unknown;
  definition: (id: string) => Row | null;
  hasStatus: (row: Row, key: string) => boolean;
}, speed = latchWalkSpeed(row, read)): boolean {
  return Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_dead') ?? 0) === 0
    && speed > 0 && Number(read.value(row, 'no_actions') ?? 0) === 0
    && !statusPreventsActions({ ...row, statuses: { target: read.statuses(row) } }, read)
    && !['unconscious', 'incapacitated', 'stunned', 'paralyzed', 'petrified', 'restrained', 'grappled'].some(key => read.hasStatus(row, key));
}
export const latchedStatuses = (row: Row, read?: StatusReader): Row[] => (read ? read.statuses(row) : Object.values(readCreatureStatuses(row)) as Row[]).filter(status =>
  statusItemIdOf(status) === LATCHED_STATUS_ID && Number(status.status_deleted ?? 0) === 0);
export const latchedObject = (row: Row, read?: StatusReader): string => {
  const status = latchedStatuses(row, read)[0];
  return status ? upper(readStatusCasterId(status, creatureId(row))) : '';
};
export const latchedCreatures = (encounter: Row, objectId: string, read?: StatusReader): Row[] =>
  (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : []).filter(row =>
    Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_deleted') ?? 0) === 0 && latchedObject(row, read) === upper(objectId));
export const latchPoint = (row: Row) => ({ x: posCoord(row, 'x_pos') ?? 0, y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0 });
